import React from 'react'
import { Col, Flex, Form, Input, Row, Select, Tooltip, Typography } from 'antd'
import { Controller, FieldErrors } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { inputNumber } from 'src/app/shared/utils'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import './index.scss'

interface ClientInfoProps {
  control: any
  errors: FieldErrors<any>
  getValues: any
  displayDuplicateText: boolean
}

function ClientInfo(props: ClientInfoProps) {
  const { control, errors } = props
  const { t } = useTranslation()

  const timeZoneList = useSelector((state: any) => state?.masterSetup?.timeZoneList)

  return (
    <Flex vertical>
      <Row gutter={24}>
        <Col span={24} md={12}>
          <Form.Item label={t('ClientSetup:addNewClient.label.clientName')} className='isRequired'>
            <Controller
              name='clientName'
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  status={!!fieldState.error ? 'error' : ''}
                  placeholder={t('ClientSetup:addNewClient.placeholder.clientName')}
                  onChange={(e) => {
                    field.onChange(e?.target?.value)
                  }}
                  onBlur={(e) => {
                    field.onChange(e?.target?.value?.trim())
                  }}
                  maxLength={200}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='clientName' />
            </Typography>
          </Form.Item>

          <Form.Item label={t('ClientSetup:addNewClient.label.referenceCode')}>
            <Controller
              name='referenceCode'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('ClientSetup:addNewClient.placeholder.referenceCode')}
                  maxLength={25}
                  onBlur={(e) => {
                    field.onChange(e?.target?.value?.trim())
                  }}
                />
              )}
            />
          </Form.Item>

          <Form.Item label={t('ClientSetup:addNewClient.label.timeZone')} className='isRequired'>
            <Controller
              name='timeZoneId'
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={timeZoneList?.map((item: any) => ({
                    ...item,
                    value: item?.id,
                    label: item?.applicationName,
                  }))}
                />
              )}
              disabled
            />
          </Form.Item>
        </Col>

        <Col span={24} md={12}>
          <Form.Item label={t('ClientSetup:addNewClient.label.clientID')} className='isRequired'>
            <Controller
              name='clientNumber'
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  status={!!fieldState.error ? 'error' : ''}
                  placeholder={t('ClientSetup:addNewClient.placeholder.clientID')}
                  maxLength={200}
                  onChange={(e) => {
                    // NOTE: GBBM-400
                    // field.onChange(inputNumber(e), { shouldValidate: true })
                    field.onChange(e, { shouldValidate: true })
                  }}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='clientNumber' />
            </Typography>
          </Form.Item>

          <Form.Item
            label={
              <Tooltip
                placement='right'
                title={t('ClientSetup:addNewClient.tooltip.clientNotes')}
                color='white'
                trigger='hover'
                arrow={false}
              >
                <Flex gap={9}>
                  {t('ClientSetup:addNewClient.label.clientNotes')}
                  <ExclamationCircleOutlined />
                </Flex>
              </Tooltip>
            }
          >
            <Controller
              name='clientNotes'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('ClientSetup:addNewClient.placeholder.clientNotes')}
                  maxLength={255}
                  onBlur={(e) => {
                    field.onChange(e?.target?.value?.trim())
                  }}
                />
              )}
            />
          </Form.Item>

          <Form.Item label={t('ClientSetup:addNewClient.label.exceptionReminderEmailTime')} className='isRequired'>
            <Controller
              name='exceptionReminderEmailTimeInMinutes'
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  className='ant-input-view-only'
                  status={!!fieldState.error ? 'error' : ''}
                  min={30}
                  max={180}
                  onChange={(e) => {
                    field.onChange(inputNumber(e), { shouldValidate: true })
                  }}
                />
              )}
              disabled
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='exceptionReminderEmailTimeInMinutes' />
            </Typography>
          </Form.Item>
        </Col>
      </Row>
    </Flex>
  )
}

export default ClientInfo
